import { useEffect, useState, useCallback } from 'react'
import { Radio, Plus, Trash2, ExternalLink, Copy, Check, RefreshCw, ArrowLeftRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { overlayAPI, tournamentsAPI, gameServersAPI } from '@/lib/api'

/* Overlay-kanalen (F1-overlay).
   Elk kanaal is één stabiele URL die OBS opent als browser-source.
   Wat er in dat kanaal getoond wordt (welke server + welke match + welke
   layout) wisselt live vanuit deze regie-tab. */

const LAYOUTS = [
  { v: 'auto',       label: 'Auto (per game)' },
  { v: 'scoreboard', label: 'Scoreboard' },
  { v: 'killfeed',   label: 'Kill-feed only' },
  { v: 'laptimes',   label: 'Lap-times' },
  { v: 'minimal',    label: 'Minimaal (naam + score)' },
  { v: 'off',        label: 'Off (niks tonen)' },
]

export default function OverlayChannelsPanel() {
  const [channels, setChannels] = useState([])
  const [servers, setServers]   = useState([])
  const [tournaments, setTours] = useState([])   // {id, name, matches[]}
  const [loading, setLoading]   = useState(true)
  const [busy, setBusy]         = useState(null)

  const laad = useCallback(async () => {
    try {
      const [c, s, t] = await Promise.all([
        overlayAPI.channels(),
        gameServersAPI.getAllServers(),
        tournamentsAPI.getTournaments(),
      ])
      setChannels(c.data?.channels || [])
      setServers((s.data?.servers || s.data || []).filter(x => x.overlay_identifier))
      // Details ophalen voor elk actief toernooi zodat we z'n matches kennen
      const alle = t.data?.tournaments || []
      const relevant = alle.filter(x => ['registration','ready','active','in_progress'].includes(x.status))
      const details = await Promise.all(
        relevant.map(x => tournamentsAPI.getTournament(x.id).then(r => r.data).catch(() => null))
      )
      setTours(details.filter(Boolean))
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { laad() }, [laad])

  const nieuwKanaal = async () => {
    const slug = window.prompt('Slug voor nieuw kanaal (bv. "beamer2"):')
    if (!slug) return
    const name = window.prompt('Weergave-naam:', slug) || slug
    setBusy('create')
    try {
      await overlayAPI.createChannel({ slug: slug.trim(), name })
      await laad()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(null) }
  }

  const wisKanaal = async (ch) => {
    if (ch.slug === 'main') { window.alert('Main-kanaal kan niet weg.'); return }
    if (!window.confirm(`Kanaal "${ch.name}" verwijderen?`)) return
    setBusy(`del-${ch.id}`)
    try { await overlayAPI.deleteChannel(ch.id); await laad() }
    finally { setBusy(null) }
  }

  const update = async (ch, patch) => {
    setBusy(`up-${ch.id}`)
    try {
      await overlayAPI.updateChannel(ch.id, patch)
      await laad()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(null) }
  }

  if (loading) return null

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Radio className="h-4 w-4" /> OBS-overlay kanalen
        </CardTitle>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={laad}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={nieuwKanaal} disabled={busy === 'create'}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Nieuw
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Elk kanaal krijgt een stabiele URL die OBS als browser-source opent.
          Wissel context hier — OBS hoeft niet te refreshen.
        </p>
        {channels.length === 0 && (
          <p className="text-sm text-muted-foreground">Nog geen kanalen. Klik "Nieuw".</p>
        )}
        {channels.map(ch => (
          <ChannelKaart key={ch.id} ch={ch} servers={servers} tournaments={tournaments}
                        onUpdate={patch => update(ch, patch)}
                        onDelete={() => wisKanaal(ch)}
                        busy={busy} />
        ))}
      </CardContent>
    </Card>
  )
}

function ChannelKaart({ ch, servers, tournaments, onUpdate, onDelete, busy }) {
  const [copied, setCopied] = useState(false)
  const overlayUrl = `${window.location.origin}/overlay/channel/${ch.slug}`

  // Toernooi-keuze wordt lokaal bijgehouden zolang er nog geen match
  // gekozen is (channel bewaart alleen match_id, niet tournament_id).
  // Zodra een match is gekozen kan het toernooi weer uit ch.match_id
  // worden afgeleid.
  const derivedTournamentId = (() => {
    for (const t of tournaments) {
      if ((t.matches || []).some(m => m.id === ch.match_id)) return t.tournament.id
    }
    return null
  })()
  const [pendingTournamentId, setPendingTournamentId] = useState(null)
  const selectedTournamentId = derivedTournamentId ?? pendingTournamentId
  const selectedTournament = tournaments.find(t => t.tournament.id === selectedTournamentId)
  const matches = selectedTournament?.matches || []

  const kopieer = () => {
    navigator.clipboard?.writeText(overlayUrl)
    setCopied(true); setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-widest text-acid">{ch.slug}</span>
        <span className="font-semibold text-sm">{ch.name}</span>
        {ch.server_name && (
          <span className="text-xs text-muted-foreground">
            · {ch.server_name}
          </span>
        )}
        {ch.tournament_name && ch.match_round && (
          <span className="text-xs text-muted-foreground">
            · {ch.tournament_name} R{ch.match_round}·M{ch.match_number}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={kopieer} title="OBS browser-source URL kopiëren">
            {copied ? <Check className="h-3.5 w-3.5 text-live" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" asChild title="Preview openen">
            <a href={overlayUrl + '?debug=1'} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
          {ch.slug !== 'main' && (
            <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy === `del-${ch.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* OBS URL onder de titel-regel voor snelle copy-inspectie */}
      <div className="text-[10px] font-mono text-muted-foreground bg-muted/30 px-2 py-1 rounded break-all">
        {overlayUrl}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {/* Server */}
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Game-server</span>
          <select value={ch.game_server_id || ''}
                  onChange={e => onUpdate({ game_server_id: e.target.value ? Number(e.target.value) : null })}
                  className="w-full h-8 text-sm bg-background border border-border rounded px-2">
            <option value="">— geen —</option>
            {servers.map(s => (
              <option key={s.id} value={s.id}>
                {s.display_name || s.name} ({s.game_type})
              </option>
            ))}
          </select>
        </label>

        {/* Toernooi — lokaal bijgehouden tot een match gekozen is */}
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Toernooi</span>
          <select value={selectedTournamentId || ''}
                  onChange={e => {
                    const tid = e.target.value ? Number(e.target.value) : null
                    setPendingTournamentId(tid)
                    // Bij wissel van toernooi: bestaande match-koppeling
                    // ontbinden (past niet meer bij dit toernooi).
                    if (ch.match_id) onUpdate({ match_id: null })
                  }}
                  className="w-full h-8 text-sm bg-background border border-border rounded px-2">
            <option value="">— geen —</option>
            {tournaments.map(t => (
              <option key={t.tournament.id} value={t.tournament.id}>{t.tournament.name}</option>
            ))}
          </select>
        </label>

        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Match</span>
          <select value={ch.match_id || ''}
                  onChange={e => {
                    const mid = e.target.value ? Number(e.target.value) : null
                    onUpdate({ match_id: mid })
                    // Match gekozen → tournament is nu afleidbaar, pending mag weg.
                    if (mid) setPendingTournamentId(null)
                  }}
                  disabled={!matches.length}
                  className="w-full h-8 text-sm bg-background border border-border rounded px-2">
            <option value="">— geen —</option>
            {matches.map(m => (
              <option key={m.id} value={m.id}>
                R{m.round}·M{m.match_number} · {m.participant1_name || '?'} vs {m.participant2_name || '?'}
              </option>
            ))}
          </select>
        </label>

        {/* Layout */}
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Layout</span>
          <select value={ch.layout}
                  onChange={e => onUpdate({ layout: e.target.value })}
                  className="w-full h-8 text-sm bg-background border border-border rounded px-2">
            {LAYOUTS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
          </select>
        </label>

        {/* Side-swap (alleen voor CS 1.6 zichtbaar — andere games hebben geen sides) */}
        {ch.server_game === 'cs16' && (
          <div className="text-xs space-y-1">
            <span className="text-muted-foreground">Sides</span>
            <button
              onClick={() => onUpdate({ sides_swapped: !ch.sides_swapped })}
              className={`w-full h-8 text-xs rounded border flex items-center justify-center gap-1.5 transition-colors ${
                ch.sides_swapped
                  ? 'border-live bg-live/15 text-live'
                  : 'border-border hover:border-acid hover:text-acid'
              }`}
              title={ch.sides_swapped
                ? 'Halftime-swap actief: Team A staat op T-side, Team B op CT-side. Klik om terug te draaien.'
                : 'Normale start: Team A op CT-side, Team B op T-side. Klik na halftime.'}>
              <ArrowLeftRight className="h-3 w-3" />
              {ch.sides_swapped ? 'Swap: A↔T · B↔CT' : 'Normaal: A=CT · B=T'}
            </button>
          </div>
        )}

        {/* Chroma */}
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Chroma</span>
          <div className="flex gap-1">
            <input type="color" value={ch.chroma || '#00FF00'}
                   onChange={e => onUpdate({ chroma: e.target.value })}
                   className="h-8 w-14 rounded border border-border cursor-pointer" />
            <Input value={ch.chroma || '#00FF00'}
                   onChange={e => onUpdate({ chroma: e.target.value })}
                   className="h-8 text-xs flex-1" />
          </div>
        </label>
      </div>

      {/* Preview */}
      <details className="text-xs">
        <summary className="text-muted-foreground cursor-pointer">Live preview</summary>
        <div className="mt-2 border border-border rounded overflow-hidden"
             style={{ background: ch.chroma || '#00FF00' }}>
          <iframe src={overlayUrl}
                  title={`Overlay preview ${ch.slug}`}
                  className="w-full"
                  style={{ height: 200, border: 'none' }} />
        </div>
      </details>
    </div>
  )
}
